<template>
  <b-modal v-model="visible" size="md" centered hide-header hide-footer
           no-close-on-backdrop body-class="p-0" content-class="ubh-content">
    <!-- Hero -->
    <div class="ubh__hero">
      <div class="ubh__hero-icon"><i class="fa-solid fa-eye-slash"></i></div>
      <div class="ubh__hero-text">
        <h4 class="mb-0">{{ $t('users.bulkHide.title', { count: targets.length }) }}</h4>
        <small>{{ namesSummary }}</small>
      </div>
      <button type="button" class="ubh__close" @click="close" :disabled="running">
        <feather-icon icon="XIcon" size="18"/>
      </button>
    </div>

    <div class="ubh__body">
      <b-alert show variant="warning" class="p-75 mb-0">
        <i class="fa-solid fa-circle-info mr-25"></i>{{ $t('users.bulkHide.warning') }}
      </b-alert>

      <!-- Progress -->
      <div v-if="running || progress.total > 0" class="ubh__progress mt-1">
        <b-progress :value="progress.done" :max="progress.total" height="8px"
                    :variant="progress.fail ? 'danger' : 'success'"/>
        <small class="text-muted">
          <template v-if="running">{{ $t('finance.actionModal.running', { done: progress.done, total: progress.total }) }}</template>
          <template v-else>
            {{ $t('finance.actionModal.resultOk', { count: progress.ok }) }}<template v-if="progress.fail"> · <span class="text-danger">{{ $t('finance.actionModal.resultFail', { count: progress.fail }) }}</span></template>
          </template>
        </small>
      </div>

      <!-- Footer -->
      <div class="ubh__footer">
        <b-button variant="outline-secondary" :disabled="running" @click="close">
          {{ $t('operations.cancel') }}
        </b-button>
        <b-button variant="warning" :disabled="running || !targets.length" @click="run">
          <b-spinner v-if="running" small class="mr-25"/>
          <i v-else class="fa-solid fa-eye-slash mr-25"></i>
          {{ $t('users.hide') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'

/**
 * Bulk soft-hide for the users table: same visual language as the monetary
 * bulk-actions modal. Nothing is deleted — the users just disappear from the
 * list (the "Show hidden" filter brings them back).
 */
export default {
  name: 'UserBulkHideModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    visible: false,
    targets: [],
    running: false,
    progress: { done: 0, total: 0, ok: 0, fail: 0 },
  }),
  computed: {
    namesSummary() {
      const names = this.targets.map(u => `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.username)
      return names.length > 5
          ? `${names.slice(0, 5).join(' · ')} · +${names.length - 5}`
          : names.join(' · ')
    },
  },
  methods: {
    ...mapActions('users', ['hideUser']),
    /** @param {Array<Object>} users selected user rows */
    open(users) {
      this.targets = (users || []).filter(Boolean)
      this.running = false
      this.progress = { done: 0, total: 0, ok: 0, fail: 0 }
      this.visible = true
    },
    close() {
      if (this.running) return
      this.visible = false
    },
    async run() {
      if (!this.targets.length || this.running) return
      this.running = true
      this.progress = { done: 0, total: this.targets.length, ok: 0, fail: 0 }
      for (const user of this.targets) {
        try {
          await this.hideUser({ id: user.id, hidden: true })
          this.progress.ok++
        } catch (err) {
          this.progress.fail++
        }
        this.progress.done++
      }
      this.running = false
      if (this.progress.fail === 0) {
        this.showSuccessNotification(this.$t('finance.actionModal.resultOk', { count: this.progress.ok }))
        this.visible = false
      } else {
        this.showWarningNotification(
            `${this.$t('finance.actionModal.resultOk', { count: this.progress.ok })} · ${this.$t('finance.actionModal.resultFail', { count: this.progress.fail })}`)
      }
      this.$emit('done')
    },
  },
}
</script>

<style lang="scss">
/* Same visual language as the monetary/finance action modals */
.ubh-content {
  border: none;
  border-radius: .8rem;
  overflow: hidden;
}
.ubh__hero {
  display: flex;
  align-items: center;
  gap: .9rem;
  padding: 1.1rem 1.4rem;
  color: #fff;
  background: linear-gradient(120deg, #7c4a03, #ff9f43);
}
.ubh__hero-icon {
  width: 44px;
  height: 44px;
  border-radius: .65rem;
  background: rgba(255, 255, 255, .18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.ubh__hero-text { flex: 1; min-width: 0; h4 { color: #fff; font-weight: 700; } small { opacity: .9; word-break: break-word; } }
.ubh__close {
  border: none;
  background: rgba(255, 255, 255, .15);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background .15s ease, transform .2s ease;
  &:hover { background: rgba(255, 255, 255, .3); transform: rotate(90deg); }
}

.ubh__body { padding: 1.15rem 1.4rem 1.3rem; }
.ubh__progress { display: flex; flex-direction: column; gap: .25rem; }
.ubh__footer {
  display: flex;
  justify-content: flex-end;
  gap: .5rem;
  margin-top: 1.1rem;
}

.dark-layout {
  .ubh-content { background: #283046; color: #d0d2d6; }
}
</style>
